import { HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { Observable, catchError, from, switchMap, throwError } from 'rxjs';

import { OutageService } from '@common/service/outage.service';
import { isDeployMarker, isOutageCandidate, isPingUrl, replayPolicy } from '@common/util/outage.util';

/**
 * AIWB-1175: holds calls that fail because the platform is unreachable (deploy / fault), confirming
 * with /api/ping first so a non-outage 5xx still surfaces normally, and replays them after recovery.
 */
export const outageInterceptor: HttpInterceptorFn = (req, next) => {
  if (isPingUrl(req.url)) {
    return next(req);
  }
  const outage = inject(OutageService);
  const attempt = (): Observable<HttpEvent<unknown>> =>
    next(req).pipe(
      catchError((error: unknown) => {
        if (!isOutageCandidate(error)) {
          return throwError(() => error);
        }
        const label = `${req.method} ${req.url} (${error.status})`;
        if (outage.dismissed) {
          return throwError(() => error); // user chose Stop waiting: no re-trap until recovery
        }
        const kind = replayPolicy(req, error.status); // 'replay' | 'uncertain' — both are held
        const deploy = isDeployMarker(error);
        if (outage.active) {
          return outage.hold(label, attempt, error, kind, deploy);
        }
        return from(outage.confirmDown()).pipe(
          switchMap((down) => (down ? outage.hold(label, attempt, error, kind, deploy) : throwError(() => error))),
        );
      }),
    );
  return attempt();
};
